import React from "react";
import ListItem, { ListItemData } from "./listItem";
import type { PaginationProps } from 'antd';
import { Pagination, Select, Input } from 'antd';
const { Search } = Input;

const data: ListItemData[] = [
    {
        name: "John Doe",
        mail: "john.doe@example.com",
        role: "admin",
        status: "active",
        plan: "free",
        avatar: "https://demos.pixinvent.com/vuexy-vuejs-admin-template/demo-1/assets/avatar-1-DMk2FF1-.png",
        endDate: new Date("2025-12-31")
    },
    {
        name: "Jane Smith",
        mail: "abc@cvd.com",
        role: "user",
        status: "inactive",
        plan: "pro",
        endDate: new Date("2025-11-30")
    },
    {
        name: "Alice Johnson",
        mail: "mnd@gid.com",
        role: "user",
        status: "deactivated",
        plan: "premium",
        endDate: new Date("2025-10-15")
    }
]

const AccountManager: React.FC = () => {
    const [selectedItems, setSelectedItems] = React.useState<number[]>([]);
    const [fillterData, setFilterData] = React.useState<ListItemData[]>(data);
    const tableId = "account-manager-table";
    //Page state management
    const [currentPage, setCurrentPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);
    const [showData, setShowData] = React.useState(fillterData.slice(0, pageSize));

    const onPageChange: PaginationProps['onChange'] = (page, pageSize) => {
        setCurrentPage(page);
        setPageSize(pageSize);
        const start = (page - 1) * pageSize;
        const end = start + pageSize;
        setShowData(data.slice(start, end));
    };

    // fillter 
    const [fillterRole, setFilterRole] = React.useState<string | undefined>(undefined);
    const [fillterPlan, setFilterPlan] = React.useState<string | undefined>(undefined);
    const [fillterStatus, setFilterStatus] = React.useState<string | undefined>(undefined);
    const filterData = React.useCallback((role?: string, plan?: string, status?: string, searchString?: string) => {
        let filtered = data;
        if (!role && !plan && !status) {
            setFilterData(data);
            setCurrentPage(1);
            setShowData(data.slice(0, pageSize));
            return;
        }
        if (!role && !plan && status) {
            filtered = data.filter(item => item.status === status);
        }
        if (!role && plan && !status) {
            filtered = data.filter(item => item.plan === plan);
        }
        if (role && !plan && !status) {
            filtered = data.filter(item => item.role === role);
        }
        if (role && plan && !status) {
            filtered = data.filter(item => item.role === role && item.plan === plan);
        }
        if (!role && !plan && status) {
            filtered = data.filter(item => item.status === status);
        }
        if (role && !plan && status) {
            filtered = data.filter(item => item.role === role && item.status === status);
        }
        if (!role && plan && status) {
            filtered = data.filter(item => item.plan === plan && item.status === status);
        }
        if (role && plan && status) {
            filtered = data.filter(item => item.role === role && item.plan === plan && item.status === status);
        }

        setFilterData(filtered);
        setCurrentPage(1);
        setShowData(filtered.slice(0, pageSize));

    }, [pageSize]);
    React.useEffect(() => {
        filterData(fillterRole, fillterPlan, fillterStatus);
    }, [fillterRole, fillterPlan, fillterStatus, filterData]);

    const downloadTableAsCSV = (tableId: string, separator: string = ',') => {
        const table = document.getElementById(tableId) as HTMLTableElement | null;
        if (!table) return;

        const rows = Array.from(table.querySelectorAll('tr'));
        const csv: string[] = [];

        for (const row of rows) {
            const cols = Array.from(row.querySelectorAll('td, th'));
            const rowData: string[] = cols.map(col => {
                let data = (col as HTMLElement).innerText.replace(/(\r\n|\n|\r)/gm, '').replace(/(\s\s)/gm, ' ');
                data = data.replace(/"/g, '""');
                return `"${data}"`;
            });
            csv.push(rowData.join(separator));
        }

        const csvString = csv.join('\n');
        const now = new Date();
        const pad = (n: number) => n.toString().padStart(2, '0');
        const filename = `export_${tableId}_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.csv`;
        const link = document.createElement('a');
        link.style.display = 'none';
        link.setAttribute('target', '_blank');
        link.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvString));
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    return (
        <div className="mt-5 rounded shadow p-3 mb-5 bg-body-tertiary">
            <h1 className="text-center mb-4">Account Manager</h1>

            <hr />
            <div className="flex sm:flex-row flex-col justify-around items-center sm:gap-10 gap-3 mb-4">
                <Select
                    allowClear
                    className="w-full !h-[40px]"
                    placeholder="Select Role"
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    value={fillterRole}
                    onChange={(value) => {
                        setFilterRole(value as string);
                    }}
                    onClear={() => {
                        setFilterRole(undefined);
                    }}
                    options={[
                        { value: 'admin', label: 'Admin' },
                        { value: 'user', label: 'User' },
                    ]}
                />
                <Select
                    allowClear
                    className="w-full !h-[40px]"
                    placeholder="Select Plan"
                    value={fillterPlan}
                    onChange={(value) => {
                        setFilterPlan(value as string);
                    }}
                    onClear={() => {
                        setFilterPlan(undefined);
                    }}
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={[
                        { value: 'free', label: 'Free' },
                        { value: 'pro', label: 'Pro' },
                        { value: 'premium', label: 'Premium' },
                    ]}
                />
                <Select
                    allowClear
                    className="w-full !h-[40px]"
                    placeholder="Select Status"
                    value={fillterStatus}
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(value) => {
                        setFilterStatus(value as string);
                    }}
                    onClear={() => {
                        setFilterStatus(undefined);
                    }}
                    options={[
                        { value: 'active', label: 'Active' },
                        { value: 'inactive', label: 'Inactive' },
                        { value: 'deactivated', label: 'Deactivated' },
                    ]}
                />
            </div>
            <div className="flex justify-end mb-4 items-center gap-4 md:flex-row flex-col">
                <Search
                    placeholder="Search by Name or Email"
                    className="lg:!w-1/4 md:!w-1/3 sm:!w-1/2 w-full"
                    height={40}
                    size="large"
                    onPressEnter={(e) => {
                        const searchString = e.currentTarget.value.trim().toLowerCase();
                        if (searchString) {
                            const filteredData = fillterData.filter(item =>
                                item.name.toLowerCase().includes(searchString) ||
                                item.mail.toLowerCase().includes(searchString)
                            );
                            setCurrentPage(1);
                            setShowData(filteredData.slice(0, pageSize));
                        } else {
                            setFilterData(data);
                            setCurrentPage(1);
                            setShowData(data.slice(0, pageSize));
                        }
                    }}
                    onSearch={
                        (value) => {
                            const searchString = value.trim().toLowerCase();
                            if (searchString) {
                                const filteredData = fillterData.filter(item =>
                                    item.name.toLowerCase().includes(searchString) ||
                                    item.mail.toLowerCase().includes(searchString)
                                );
                                setCurrentPage(1);
                                setShowData(filteredData.slice(0, pageSize));
                            } else {
                                setFilterData(data);
                                setCurrentPage(1);
                                setShowData(data.slice(0, pageSize));
                            }
                        }
                    }
                    enterButton
                />
                <div className="items-center gap-2 flex ">
                    <button
                        className="btn btn-primary py-1 h-10 !rounded-lg"
                        aria-label="Export to CSV"
                        id="export-csv-button"
                        onClick={() => {
                            downloadTableAsCSV(tableId);
                        }}
                    >
                        <p className="m-0 flex flex-row items-center gap-2">
                            Export to CSV
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#fff" viewBox="0 0 256 256">
                                <path d="M224,144v64a8,8,0,0,1-8,8H40a8,8,0,0,1-8-8V144a8,8,0,0,1,16,0v56H208V144a8,8,0,0,1,16,0Zm-101.66,5.66a8,8,0,0,0,11.32,0l40-40a8,8,0,0,0-11.32-11.32L136,124.69V32a8,8,0,0,0-16,0v92.69L93.66,98.34a8,8,0,0,0-11.32,11.32Z"></path>
                            </svg>
                        </p>
                    </button>
                    <button
                        className="btn btn-secondary py-1 h-10 !rounded-lg"
                        onClick={() => {
                            setFilterData(data);
                            setCurrentPage(1);
                            setShowData(data.slice(0, pageSize));
                            setSelectedItems([]);
                            setFilterRole(undefined);
                            setFilterPlan(undefined);
                            setFilterStatus(undefined);
                        }}
                    >
                        <p className="m-0 flex flex-row items-center gap-2">
                            Reset Filters
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#000000" viewBox="0 0 256 256">
                                <path d="M225,80.4,183.6,39a24,24,0,0,0-33.94,0L31,157.66a24,24,0,0,0,0,33.94l30.06,30.06A8,8,0,0,0,66.74,224H216a8,8,0,0,0,0-16h-84.7L225,114.34A24,24,0,0,0,225,80.4ZM108.68,208H70.05L42.33,180.28a8,8,0,0,1,0-11.31L96,115.31,148.69,168Zm105-105L160,156.69,107.31,104,161,50.34a8,8,0,0,1,11.32,0l41.38,41.38a8,8,0,0,1,0,11.31Z"></path>
                            </svg>
                        </p>
                    </button>
                </div>

            </div>
            <div className="overflow-x-auto">
                <table id={tableId} className="table table-striped m-0 w-full overflow-x-scroll" >
                    <thead className="w-full border-top border-bottom border-gray-200">
                        <tr className="w-full">
                            <th scope="col" className="text-center w-[80px]">
                                <input
                                    type="checkbox"
                                    checked={selectedItems.length === showData.length && showData.length > 0}
                                    ref={input => {
                                        if (input) {
                                            input.indeterminate = selectedItems.length > 0 && selectedItems.length < showData.length;
                                        }
                                    }}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedItems(showData.map((_, index) => index));
                                        } else {
                                            setSelectedItems([]);
                                        }
                                    }}
                                    className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500 focus:ring-2"
                                    aria-label="Select All"
                                    style={{
                                        accentColor: selectedItems.length > 0 ? "#0d6efd" : undefined
                                    }}
                                />

                            </th>
                            <th scope="col" className="text-start w-4/12 font-normal" >
                                User
                            </th>
                            <th scope="col" className="text-start w-1/12 font-normal">
                                Role
                            </th>
                            <th scope="col" className="text-start w-1/12 font-normal">
                                Plan
                            </th>
                            <th scope="col" className="text-start w-2/12 font-normal">
                                Expiry in
                            </th>
                            <th scope="col" className="text-start w-1/12 font-normal">
                                Status
                            </th>
                            <th scope="col" className="text-center w-2/12 font-normal" >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {showData.map((item, index) => (
                            <ListItem
                                key={index}
                                item={{ ...item, index }}
                                selected={selectedItems.includes(index)}
                                onDelete={(index) => console.log("Delete", index)}
                                onDetail={(index) => console.log("Detail", index)}
                                onEdit={(index) => console.log("Edit", index)}
                                onSelected={(index) =>
                                    setSelectedItems([...selectedItems, index])
                                }
                                onUnselected={(index) =>
                                    setSelectedItems(selectedItems.filter(i => i !== index))
                                }
                            />
                        ))}
                    </tbody>
                </table>
            </div>
            <div>
                <Pagination
                    className="flex justify-end mt-4"
                    current={currentPage}
                    pageSize={pageSize}

                    total={fillterData.length}
                    onChange={onPageChange}
                />
                <br />
            </div>
        </div>
    );
}

export default AccountManager;

